import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { CAMPO_BASE } from "./ui";
import AdminAvaliacoesVisao from "./AdminAvaliacoesVisao";
import AdminAvaliacoesFeedbacks from "./AdminAvaliacoesFeedbacks";
import AdminAvaliacoesFerramentas from "./AdminAvaliacoesFerramentas";
import AdminAvaliacoesComparacao from "./AdminAvaliacoesComparacao";
import {
  PERIODOS,
  buscaComPeriodo,
  buscaSoDoPeriodo,
  diaBr,
  pedidoDaBusca,
  type ChaveDoPeriodo,
  type PedidoDePeriodo,
} from "../lib/avaliacoes";

/**
 * Admin › Avaliações — o painel das avaliações das ferramentas (000327/000328).
 *
 * Nasceu em 29/09 com duas sub-abas (Ferramentas e Comparação), a pedido de
 * Victor, logo depois de as avaliações passarem a morar no hub (000326, F9). No
 * mesmo dia ele pediu que ela tivesse *"as mesmas abas da central de
 * feedbacks"* — o painel `feedbacks-cf`, que o time de Produtos lia:
 *
 *   · **Visão geral** — os números do período contra o anterior, quem está em
 *     atenção, a evolução, as distribuições, as avaliações baixas recentes e o
 *     diagnóstico por ferramenta;
 *   · **Feedbacks** — cada avaliação recebida, com filtros e a planilha;
 *   · **Ferramentas** — todas lado a lado, com a meta de cada uma e a edição
 *     dela ali mesmo. A página de UMA (`?card=<slug>`) mora aqui;
 *   · **Comparar** — até quatro, no tempo e lado a lado.
 *
 * A sub-aba é SUB-ROTA e o período mora na QUERY, pela mesma razão do
 * Catálogo: o operador volta pelo botão do navegador e manda o link de uma
 * tela a um colega.
 *
 * ---------------------------------------------------------------------------
 * O período vale para as QUATRO, e atravessa de uma para outra
 * ---------------------------------------------------------------------------
 * No painel antigo o filtro de período era da página (o Dashboard e os
 * Feedbacks tinham o seu; Ferramentas e Comparar eram de todo o tempo). Aqui
 * ele é UM, acima das sub-abas, e os links entre elas o levam junto: a
 * ferramenta que a Visão geral põe em atenção nos 30 dias abre com a média dos
 * MESMOS 30 dias — com dois relógios, o número do clique não seria o da página.
 * Os links de uma sub-aba para outra levam SÓ o período: os filtros de uma não
 * significam nada na outra.
 *
 * ---------------------------------------------------------------------------
 * ZERO conta aqui
 * ---------------------------------------------------------------------------
 * Cada sub-aba lê o seu conjunto no banco (`lib/avaliacoes.ts`) e declara de
 * que período são os números que mostra. Este arquivo só despacha e desenha a
 * barra do período — e nem ela sabe que dia é hoje: quem resolve "30 dias" é o
 * banco, no fuso de São Paulo.
 */

type Sub = "visao" | "feedbacks" | "ferramentas" | "comparar";

const SUBS: Array<{ id: Sub; caminho: string; rotulo: string; nota: string }> = [
  { id: "visao", caminho: "", rotulo: "Visão geral", nota: "os números e os gráficos" },
  { id: "feedbacks", caminho: "/feedbacks", rotulo: "Feedbacks", nota: "cada avaliação recebida" },
  { id: "ferramentas", caminho: "/ferramentas", rotulo: "Ferramentas", nota: "as metas de cada uma" },
  { id: "comparar", caminho: "/comparar", rotulo: "Comparar", nota: "até quatro lado a lado" },
];

/** O caminho com a busca, sem a interrogação sobrando quando ela é vazia. */
function comBusca(caminho: string, busca: string): string {
  return busca ? `${caminho}?${busca}` : caminho;
}

function subDaRota(sub: string | undefined): Sub | null {
  if (!sub) return "visao";
  return SUBS.some((s) => s.id === sub) && sub !== "visao" ? (sub as Sub) : null;
}

/**
 * `podeEditar` e `veCatalogo` vêm do nível que o BANCO calculou
 * (`minhas_permissoes`). É desenho, não trava: quem recusa a meta de quem só
 * vê é `av_admin_definir_meta`, com a frase que diz como pedir a edição.
 *
 * `secoes` é a troca entre as duas seções da aba Feedbacks (000332): esta e as
 * pesquisas de satisfação. Ela mora na LINHA do título, e não acima dele, de
 * propósito: a Visão geral tem de caber na primeira dobra a 1280px, e medido em
 * 30/09 a folga do título da atenção na janela real era de 18px. Sem a prop, a
 * tela é a mesma de antes.
 */
export default function AdminAvaliacoes({
  podeEditar,
  veCatalogo,
  secoes,
}: {
  podeEditar: boolean;
  veCatalogo: boolean;
  secoes?: ReactNode;
}) {
  const { sub } = useParams();
  const [busca, setBusca] = useSearchParams();
  const pedido = pedidoDaBusca(busca);

  // Os endereços da primeira versão (000327), que moraram algumas horas: a
  // página de uma ferramenta era `/admin/avaliacoes?card=`, e a comparação,
  // `/comparacao`. Um só endereço por tela — o velho vira o novo.
  if (!sub && busca.get("card")) return <Navigate to={`/admin/avaliacoes/ferramentas?${busca.toString()}`} replace />;
  if (sub === "comparacao") return <Navigate to={comBusca("/admin/avaliacoes/comparar", busca.toString())} replace />;

  const atual = subDaRota(sub);
  if (!atual) return <Navigate to={comBusca("/admin/avaliacoes", buscaSoDoPeriodo(pedido))} replace />;

  function mudarPeriodo(p: PedidoDePeriodo) {
    // A página de uma lista paginada volta à primeira: a página 3 de um período
    // não é a página 3 de outro.
    const semPagina = new URLSearchParams(busca);
    semPagina.delete("pagina");
    setBusca(buscaComPeriodo(semPagina, p));
  }

  const soDoPeriodo = buscaSoDoPeriodo(pedido);

  return (
    <div data-painel-avaliacoes>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h1 className="font-display text-2xl font-bold text-titulo">Avaliações das ferramentas</h1>
        {secoes}
      </div>
      <p className="mt-1 max-w-3xl text-sm leading-relaxed text-cinza">
        O que os alunos dizem de cada ferramenta: a média das três notas (utilidade, facilidade e satisfação), a meta que
        o time definiu e quantas avaliações sustentam cada número.
      </p>

      <nav className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Painéis das avaliações">
        {SUBS.map((s) => (
          <Link
            key={s.id}
            to={comBusca(`/admin/avaliacoes${s.caminho}`, soDoPeriodo)}
            role="tab"
            aria-selected={atual === s.id}
            data-subaba={s.id}
            className={`min-h-10 rounded-full border px-4 py-2 text-sm font-semibold transition ${
              atual === s.id ? "border-laranja bg-laranja text-azul" : "border-borda text-cinza hover:bg-chip hover:text-tinta"
            }`}
          >
            {s.rotulo}
            <span className="ml-2 hidden text-xs font-normal lg:inline">{s.nota}</span>
          </Link>
        ))}
      </nav>

      <BarraDoPeriodo pedido={pedido} aoMudar={mudarPeriodo} />

      <div className="mt-6">
        {atual === "visao" && <AdminAvaliacoesVisao pedido={pedido} />}
        {atual === "feedbacks" && <AdminAvaliacoesFeedbacks pedido={pedido} />}
        {atual === "ferramentas" && <AdminAvaliacoesFerramentas pedido={pedido} podeEditar={podeEditar} veCatalogo={veCatalogo} />}
        {atual === "comparar" && <AdminAvaliacoesComparacao pedido={pedido} />}
      </div>
    </div>
  );
}

// ── A barra do período ──────────────────────────────────────────────────────

/**
 * O dia de hoje no fuso de São Paulo, como AAAA-MM-DD, SÓ para preencher e
 * limitar os campos de data do personalizado. Não decide período nenhum: quem
 * decide (e recusa uma data final depois de hoje) é o banco. O `sv-SE` é o
 * idioma cujo formato de data é o ISO.
 */
function diaDeSaoPaulo(deslocamentoEmDias = 0): string {
  const d = new Date(Date.now() + deslocamentoEmDias * 86_400_000);
  return d.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
}

function BarraDoPeriodo({ pedido, aoMudar }: { pedido: PedidoDePeriodo; aoMudar: (p: PedidoDePeriodo) => void }) {
  const idDoFormulario = useId();
  const personalizado = pedido.chave === "personalizado";
  // O formulário do personalizado abre no clique, e só vira período no
  // "Aplicar": com as duas datas mudando uma de cada vez, cada campo trocado
  // dispararia uma leitura de um período que ninguém pediu.
  const [aberto, setAberto] = useState(personalizado && (!pedido.de || !pedido.ate));
  const [de, setDe] = useState(pedido.de ?? diaDeSaoPaulo(-29));
  const [ate, setAte] = useState(pedido.ate ?? diaDeSaoPaulo());

  // Quando a URL muda por fora (o botão de voltar), os campos acompanham.
  useEffect(() => {
    if (pedido.de) setDe(pedido.de);
    if (pedido.ate) setAte(pedido.ate);
  }, [pedido.de, pedido.ate]);

  function escolher(chave: ChaveDoPeriodo) {
    if (chave === "personalizado") {
      setAberto((a) => !a);
      return;
    }
    setAberto(false);
    aoMudar({ chave, de: null, ate: null });
  }

  function aplicar(e: FormEvent) {
    e.preventDefault();
    aoMudar({ chave: "personalizado", de, ate });
    setAberto(false);
  }

  return (
    <div className="mt-4" data-barra-do-periodo>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Período das avaliações">
        <span className="text-xs font-semibold text-cinza">Período</span>
        {PERIODOS.map((p) => {
          const escolhido = pedido.chave === p.chave;
          const rotulo =
            p.chave === "personalizado" && personalizado && pedido.de && pedido.ate
              ? `${p.rotulo}: ${diaBr(pedido.de)} a ${diaBr(pedido.ate)}`
              : p.rotulo;
          return (
            <button
              key={p.chave}
              type="button"
              aria-pressed={escolhido}
              aria-expanded={p.chave === "personalizado" ? aberto : undefined}
              aria-controls={p.chave === "personalizado" ? idDoFormulario : undefined}
              onClick={() => escolher(p.chave)}
              data-periodo-do-painel={p.chave}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                escolhido ? "border-acento bg-acento text-acento-tinta" : "border-borda bg-superficie text-tinta hover:border-acento"
              }`}
            >
              {rotulo}
            </button>
          );
        })}
      </div>
      {aberto && (
        <form
          id={idDoFormulario}
          onSubmit={aplicar}
          className="mt-3 flex flex-wrap items-end gap-3 rounded-xl border border-borda bg-superficie p-3"
          data-periodo-personalizado
        >
          <label className="grid gap-1 text-xs font-semibold text-cinza">
            De
            <input
              type="date"
              value={de}
              max={ate || undefined}
              onChange={(e) => setDe(e.target.value)}
              required
              className={`${CAMPO_BASE} w-44`}
              data-periodo-de
            />
          </label>
          <label className="grid gap-1 text-xs font-semibold text-cinza">
            Até
            <input
              type="date"
              value={ate}
              min={de || undefined}
              max={diaDeSaoPaulo()}
              onChange={(e) => setAte(e.target.value)}
              required
              className={`${CAMPO_BASE} w-44`}
              data-periodo-ate
            />
          </label>
          <button type="submit" className="pill-cta min-h-12 px-5" data-aplicar-periodo>
            Aplicar
          </button>
          <p className="basis-full text-xs text-cinza">
            Os dois dias entram, de meia-noite a meia-noite em São Paulo. A comparação é com o mesmo número de dias logo
            antes.
          </p>
        </form>
      )}
    </div>
  );
}
